import { API_CONFIG, buildApiUrl, getAuthHeaders } from '../config/api.ts';
import { apiRequest } from '../utils/apiRequest.ts';

// Types for Inventory Vendors API
export interface InventoryVendor {
  id: number;
  vendorName: string;
  accountNo: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  paymentTerms: string | null;
  activeFlag: boolean;
  userId: number;
  createdDate: string | number;
}

export interface CreateInventoryVendorRequest {
  vendorName: string;
  accountNo?: string;
  contactName?: string;
  phone?: string;
  email?: string;
  paymentTerms?: string;
  activeFlag?: boolean;
}

export interface PaginatedInventoryVendorsResponse {
  data: InventoryVendor[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface InventoryVendorsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  activeFlag?: boolean;
}

export const inventoryVendorsApi = {
  // Get all vendors with pagination
  getVendors: async (
    params: InventoryVendorsQueryParams = {},
    token: string
  ): Promise<PaginatedInventoryVendorsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.activeFlag !== undefined) searchParams.append('activeFlag', String(params.activeFlag));

    const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.INVENTORY_VENDORS.BASE)}?${searchParams.toString()}`;

    return apiRequest<PaginatedInventoryVendorsResponse>(url, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
  },

  // Create new vendor
  createVendor: async (data: CreateInventoryVendorRequest, token: string): Promise<InventoryVendor> => {
    return apiRequest<InventoryVendor>(
      buildApiUrl(API_CONFIG.ENDPOINTS.INVENTORY_VENDORS.BASE),
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  // Update vendor (admin only)
  updateVendor: async (
    id: number,
    data: Partial<CreateInventoryVendorRequest>,
    token: string
  ): Promise<InventoryVendor> => {
    return apiRequest<InventoryVendor>(
      buildApiUrl(API_CONFIG.ENDPOINTS.INVENTORY_VENDORS.BY_ID(id)),
      {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  // Delete vendor (admin only)
  deleteVendor: async (id: number, token: string): Promise<void> => {
    return apiRequest<void>(
      buildApiUrl(API_CONFIG.ENDPOINTS.INVENTORY_VENDORS.BY_ID(id)),
      {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      }
    );
  },
};
